import React, { useEffect, useState } from 'react';
import { X, Sparkles, Send } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * Seller reply to ONE buyer (scoped by conversation_id). Ticket 266314: the seller's
 * text is sent to the buyer exactly as written -- no AI call, no rewrite.
 */
export default function SellerAdviceModal({ conversationId, onClose, onResolved }) {
  const [conv, setConv] = useState(null);
  const [instruction, setInstruction] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!conversationId) return;
    api.get(`/ai/conversations/${conversationId}`)
      .then(({ data }) => setConv(data))
      .catch(() => setConv({ messages: [] }));
  }, [conversationId]);

  const msgs = (conv?.messages || []).slice(-8);
  const lastBuyer = [...(conv?.messages || [])].reverse().find((m) => m.direction === 'INBOUND');

  const submit = async () => {
    if (!instruction.trim()) return toast.error('Enter your reply to the buyer');
    setSending(true);
    try {
      const { data } = await api.post(`/ai/conversations/${conversationId}/seller-advice`, { instruction: instruction.trim() });
      toast.success('Your reply was sent to the buyer');
      onResolved?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send your reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="seller-advice-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[88vh]">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-fab-primary-lighter">
          <div className="flex items-center gap-2 text-[14px] font-bold text-fab-text">
            <Sparkles className="w-4 h-4 text-fab-primary" /> Reply to buyer
          </div>
          <button onClick={onClose} data-testid="seller-advice-close" className="p-1 text-fab-text-muted hover:text-fab-text"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-fab-primary-lighter text-[12px] text-fab-text-secondary">
          <div><span className="font-semibold text-fab-text">Buyer:</span> {conv?.customer_name || conv?.customer_phone || 'Buyer'}{conv?.customer_phone ? ` · ${conv.customer_phone}` : ''}</div>
          {lastBuyer && <div className="mt-1"><span className="font-semibold text-fab-text">Latest request:</span> "{lastBuyer.content}"</div>}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2 bg-[#f7f4ef]">
          {msgs.map((m, i) => (
            <div key={i} className={`text-[12.5px] max-w-[85%] px-3 py-2 rounded-xl ${m.direction === 'INBOUND' ? 'bg-white' : 'bg-fab-primary/10 ml-auto'}`} data-testid={`advice-msg-${i}`}>
              {m.content}
              {m.media_url && (
                <a href={m.media_url} target="_blank" rel="noreferrer" data-testid={`advice-media-${i}`}
                   className="block mt-1 text-fab-primary underline text-[12px]">
                  View buyer {String(m.type || 'media').toLowerCase() === 'audio' ? 'voice message' : String(m.type || 'media').toLowerCase()}
                </a>
              )}
            </div>
          ))}
          {msgs.length === 0 && <div className="text-[12px] text-fab-text-muted text-center py-4">No conversation history.</div>}
        </div>

        <div className="px-5 py-4 border-t border-fab-primary-lighter space-y-2">
          <div className="text-[11px] text-fab-text-muted">Your reply is sent to the buyer exactly as you write it. The AI does not change it.</div>
          <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} data-testid="seller-advice-input" rows={2} placeholder='Type your reply to the buyer' className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] resize-none" />
          <button onClick={submit} disabled={sending} data-testid="seller-advice-submit" className="btn-primary w-full flex items-center justify-center gap-2 text-[13px]">
            <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send reply to buyer'}
          </button>
        </div>
      </div>
    </div>
  );
}
